// ✅ src/pages/VehicleV2/HireVehicleDetailsEntry.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { FaSave, FaArrowLeft, FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/hireDetailsEntry";

// ✅ robust convert for select values
const sVal = (v) => (v === null || v === undefined ? "" : String(v));

// ✅ date => input yyyy-mm-dd
const toInputDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ✅ read query string keys from BOTH cases (vehicleid / vehicleId etc.)
const readQS = () => {
  const sp = new URLSearchParams(window.location.search);

  const getAny = (...keys) => {
    for (const k of keys) {
      const v = sp.get(k);
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
    }
    return "";
  };

  return {
    vehicleId: getAny("vehicleId", "vehicleid", "VehicleId", "VehicleID"),
    vehicledetid: getAny("vehicledetid", "vehicleDetId", "vehicledetId", "VehicleDetId"),
    vehicleCode: getAny("vehicleCode", "vehiclecode", "VehicleCode"),
  };
};

const HireVehicleDetailsEntry = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const qs = useMemo(() => readQS(), []);
  const editVehicleId = Number(qs.vehicleId || 0);
  const editVehDetId = Number(qs.vehicledetid || 0);

  // ✅ localStorage values in same style as other updated pages
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const branchId =
    Number(localStorage.getItem("branchId") || localStorage.getItem("branchid") || 8);
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [loading, setLoading] = useState(false);
  const [ddlLoaded, setDdlLoaded] = useState(false);

  // dropdowns
  const [ddl, setDdl] = useState({
    stores: [],
    vehicleSubTypes: [],
    vehicleBasis: [],
    fuelTypes: [],
    tdsPercents: [],
    foodOptions: [
      { id: "Yes", name: "Yes" },
      { id: "No", name: "No" },
    ],
  });

  const [formData, setFormData] = useState({
    // ✅ IDs (string for selects)
    storeId: "",
    vehicleSubTypeId: "",
    vehicleBasisId: "",
    fuelTypeId: "",
    tdsPercentId: "",

    // master
    vehicleCode: "",
    vehicleName: "",
    vehicleRegistrationNumber: "",
    model: "",

    // details
    hiredFrom: "",
    engineSUNumber: "",
    vehicleSINumber: "",

    address1: "",
    address2: "",
    city: "",
    telephone: "",

    adminApprovalDate: "",
    fcExpire: "",
    icExpire: "",

    foodForDriver: "",
    chequeFavourName: "",
    remarks: "",

    vehicleRent: "",
    ownersName: "",
    contactPerson: "",
    mobileNo: "",
    validFrom: "",
    validUpto: "",
    permitExpire: "",

    panCardNumber: "",
    panHolderName: "",
    mailingAddress: "",

    // ✅ ASPX equivalent: rd_hsdfillinghr / rd_hsdfillingkm
    hasFillingHr: "no",
    hasFillingKm: "no",
  });

  const setField = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  const onChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  /* ===================== 1) DROPDOWNS ===================== */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setDdlLoaded(false);

        const res = await axios.get(`${API_BASE}/dropdowns`, {
          params: { branchId },
          headers: commonHeaders,
        });

        const data = res.data?.data || {};
        setDdl((p) => ({
          ...p,
          stores: data.stores || [],
          vehicleSubTypes: data.vehicleSubTypes || [],
          vehicleBasis: data.vehicleBasis || [],
          fuelTypes: data.fuelTypes || [],
          tdsPercents: data.tdsPercents || [],
          foodOptions: data.foodOptions || p.foodOptions,
        }));

        setDdlLoaded(true);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dropdowns");
      } finally {
        setLoading(false);
      }
    })();
  }, [branchId, commonHeaders]);

  /* ===================== 2) EDIT LOAD (AFTER DROPDOWNS) ===================== */
  useEffect(() => {
    if (!editVehicleId) return;
    if (!ddlLoaded) return;

    (async () => {
      try {
        setLoading(true);

        const res = await axios.get(`${API_BASE}/view`, {
          params: { vehicleId: editVehicleId, vehicledetid: editVehDetId },
          headers: commonHeaders,
        });

        const master = res.data?.data?.master || {};
        const det = res.data?.data?.details || {};

        const storeId = sVal(master.fld_storeid ?? master.storeId ?? "");
        const subTypeId = sVal(master.fld_vehicle_subtypeid ?? master.vehicleSubTypeId ?? "");
        const basisId = sVal(master.fld_vehicle_basis ?? master.fld_vehicle_basisid ?? "");
        const fuelTypeId = sVal(det.fld_fueltype ?? det.fld_fueltypeid ?? det.fuelTypeId ?? "");

        setFormData((p) => ({
          ...p,

          storeId,
          vehicleSubTypeId: subTypeId,
          vehicleBasisId: basisId,
          fuelTypeId,
          tdsPercentId: sVal(det.fld_TDSpercentid ?? det.fld_tdspercentid ?? ""),

          vehicleCode: sVal(master.fld_vehicle_code ?? qs.vehicleCode ?? ""),
          vehicleName: sVal(master.fld_vehicle_name ?? ""),
          vehicleRegistrationNumber: sVal(master.fld_vehicle_regno ?? ""),
          model: sVal(master.fld_vehicle_model ?? ""),

          hiredFrom: sVal(det.fld_hiredfrom ?? ""),
          engineSUNumber: sVal(det.fld_engineSLNO ?? ""),
          vehicleSINumber: sVal(det.fld_equipmentSLNO ?? ""),

          address1: sVal(det.fld_address1 ?? ""),
          address2: sVal(det.fld_address2 ?? ""),
          city: sVal(det.fld_city ?? ""),
          telephone: sVal(det.fld_telephone ?? ""),

          adminApprovalDate: toInputDate(det.fld_admin_approvaldate),
          fcExpire: toInputDate(det.fld_FCExpire),
          icExpire: toInputDate(det.fld_ICExpire),

          foodForDriver: sVal(det.fld_foodfordriver ?? ""),
          chequeFavourName: sVal(det.fld_chq_favouringname ?? ""),
          remarks: sVal(det.fld_remarks ?? ""),

          vehicleRent: sVal(det.fld_vehicle_rent ?? ""),
          ownersName: sVal(det.fld_ownersname ?? ""),
          contactPerson: sVal(det.fld_contactperson ?? ""),
          mobileNo: sVal(det.fld_mobileno ?? ""),

          validFrom: toInputDate(det.fld_validfrom),
          validUpto: toInputDate(det.fld_validupto),
          permitExpire: toInputDate(det.fld_permitdetails),

          panCardNumber: sVal(det.fld_pan_cardno ?? ""),
          panHolderName: sVal(det.fld_pan_holdername ?? ""),
          mailingAddress: sVal(det.fld_mailingaddress ?? ""),

          hasFillingHr: sVal(master.fld_hsdfillinghr ?? "0") === "1" ? "yes" : "no",
          hasFillingKm: sVal(master.fld_hsdfillingkm ?? "0") === "1" ? "yes" : "no",
        }));

        // ✅ load TDS list for edit subtype so selected TDS shows
        if (subTypeId) {
          try {
            const tdsRes = await axios.get(`${API_BASE}/tds`, {
              params: { vehicleSubTypeId: Number(subTypeId) },
              headers: commonHeaders,
            });
            setDdl((p) => ({ ...p, tdsPercents: tdsRes.data?.data || [] }));
          } catch {
            // ignore
          }
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load edit data");
      } finally {
        setLoading(false);
      }
    })();
  }, [editVehicleId, editVehDetId, ddlLoaded, qs.vehicleCode, commonHeaders]);

  /* ===================== 3) TDS RELOAD ON SUBTYPE CHANGE ===================== */
  useEffect(() => {
    if (!formData.vehicleSubTypeId) return;

    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/tds`, {
          params: { vehicleSubTypeId: Number(formData.vehicleSubTypeId) },
          headers: commonHeaders,
        });
        setDdl((p) => ({ ...p, tdsPercents: res.data?.data || [] }));
      } catch {
        // ignore
      }
    })();
  }, [formData.vehicleSubTypeId, commonHeaders]);

  /* ===================== 4) AUTO HSD FILLING BASED ON BASIS NAME ===================== */
  useEffect(() => {
    if (!formData.vehicleBasisId) return;

    const basis = ddl.vehicleBasis.find((x) => sVal(x.id) === sVal(formData.vehicleBasisId));
    const basisName = sVal(basis?.name).toLowerCase();

    if (basisName === "km") {
      setFormData((p) => ({ ...p, hasFillingKm: "yes", hasFillingHr: "no" }));
    } else if (basisName === "hour") {
      setFormData((p) => ({ ...p, hasFillingKm: "no", hasFillingHr: "yes" }));
    }
  }, [formData.vehicleBasisId, ddl.vehicleBasis]);

  const handleClear = () => {
    setFormData({
      storeId: "",
      vehicleSubTypeId: "",
      vehicleBasisId: "",
      fuelTypeId: "",
      tdsPercentId: "",

      vehicleCode: "",
      vehicleName: "",
      vehicleRegistrationNumber: "",
      model: "",

      hiredFrom: "",
      engineSUNumber: "",
      vehicleSINumber: "",

      address1: "",
      address2: "",
      city: "",
      telephone: "",

      adminApprovalDate: "",
      fcExpire: "",
      icExpire: "",

      foodForDriver: "",
      chequeFavourName: "",
      remarks: "",

      vehicleRent: "",
      ownersName: "",
      contactPerson: "",
      mobileNo: "",
      validFrom: "",
      validUpto: "",
      permitExpire: "",

      panCardNumber: "",
      panHolderName: "",
      mailingAddress: "",

      hasFillingHr: "no",
      hasFillingKm: "no",
    });
    toast.info("Form cleared!");
  };

  const validate = () => {
    const required = [
      ["storeId", "Store"],
      ["vehicleCode", "Vehicle Code"],
      ["vehicleName", "Vehicle Name"],
      ["hiredFrom", "Hired From"],
      ["vehicleSubTypeId", "Vehicle Sub Type"],
      ["vehicleBasisId", "Vehicle Basis"],
      ["fuelTypeId", "Fuel Type"],
    ];
    const missing = required.filter(([k]) => !sVal(formData[k]).trim());
    if (missing.length) {
      toast.error(`Please fill required fields: ${missing.map((x) => x[1]).join(", ")}`);
      return false;
    }
    if (!branchId || !loginUserId) {
      toast.error("branchId / loginUserId missing in localStorage");
      return false;
    }
    return true;
  };

  /* ===================== 5) SAVE (INSERT / UPDATE) ===================== */
  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      branchId,
      loginUserId,

      vehicleId: editVehicleId || 0,
      vehicledetid: editVehDetId || 0,

      // IDs
      store: Number(formData.storeId),
      vehicleSubType: Number(formData.vehicleSubTypeId),
      vehicleBasis: Number(formData.vehicleBasisId),
      fuelType: Number(formData.fuelTypeId),
      tds: formData.tdsPercentId ? Number(formData.tdsPercentId) : "",

      // master
      vehicleCode: formData.vehicleCode,
      vehicleName: formData.vehicleName,
      vehicleRegistrationNumber: formData.vehicleRegistrationNumber,
      model: formData.model,

      // details
      hiredFrom: formData.hiredFrom,
      engineSUNumber: formData.engineSUNumber,
      vehicleSINumber: formData.vehicleSINumber,

      address1: formData.address1,
      address2: formData.address2,
      city: formData.city,
      telephone: formData.telephone,

      adminApprovalDate: formData.adminApprovalDate,
      fcExpire: formData.fcExpire,
      icExpire: formData.icExpire,

      foodForDriver: formData.foodForDriver,
      chequeFavourName: formData.chequeFavourName,
      remarks: formData.remarks,

      vehicleRent: formData.vehicleRent,
      ownersName: formData.ownersName,
      contactPerson: formData.contactPerson,
      mobileNo: formData.mobileNo,
      validFrom: formData.validFrom,
      validUpto: formData.validUpto,
      permitExpire: formData.permitExpire,

      panCardNumber: formData.panCardNumber,
      panHolderName: formData.panHolderName,
      mailingAddress: formData.mailingAddress,

      // filling flags
      hasFillingHr: formData.hasFillingHr,
      hasFilling: formData.hasFillingKm,
    };

    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });
      toast.success(
        res.data?.message || (editVehicleId ? "Updated successfully!" : "Saved successfully!")
      );
      // navigate("/vehicle/hirevehicle_detailslist");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">HIRE VEHICLE DETAILS ENTRY</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex items-center justify-between mb-6">
          <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
            HIRE VEHICLE DETAILS {editVehicleId ? `(EDIT - ${qs.vehicleCode || ""})` : ""}
          </h1>
          {loading && (
            <span className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              Loading...
            </span>
          )}
        </div>

        <Section title="Basic Information" isDark={isDark}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FieldSelect
              label="Store *"
              name="storeId"
              value={formData.storeId}
              onChange={onChange}
              isDark={isDark}
              options={[{ id: "", name: "Select Store" }, ...ddl.stores]}
            />
            <FieldInput label="Vehicle Code *" name="vehicleCode" value={formData.vehicleCode} onChange={onChange} isDark={isDark} />
            <FieldInput label="Vehicle Name *" name="vehicleName" value={formData.vehicleName} onChange={onChange} isDark={isDark} />

            <FieldSelect
              label="Vehicle Sub Type *"
              name="vehicleSubTypeId"
              value={formData.vehicleSubTypeId}
              onChange={onChange}
              isDark={isDark}
              options={[{ id: "", name: "Select Vehicle Sub Type" }, ...ddl.vehicleSubTypes]}
            />
            <FieldInput label="Vehicle Registration Number" name="vehicleRegistrationNumber" value={formData.vehicleRegistrationNumber} onChange={onChange} isDark={isDark} />
            <FieldInput label="Model" name="model" value={formData.model} onChange={onChange} isDark={isDark} />
          </div>
        </Section>

        <Section title="CONTACT INFO" isDark={isDark}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldInput label="Hired From *" name="hiredFrom" value={formData.hiredFrom} onChange={onChange} isDark={isDark} />
            <FieldInput label="Engine SL No" name="engineSUNumber" value={formData.engineSUNumber} onChange={onChange} isDark={isDark} />

            <FieldInput label="Address 1" name="address1" value={formData.address1} onChange={onChange} isDark={isDark} />
            <FieldInput label="Address 2" name="address2" value={formData.address2} onChange={onChange} isDark={isDark} />

            <FieldInput label="City" name="city" value={formData.city} onChange={onChange} isDark={isDark} />
            <FieldInput label="Telephone" name="telephone" value={formData.telephone} onChange={onChange} isDark={isDark} />

            <FieldInput type="date" label="Admin Approval Date" name="adminApprovalDate" value={formData.adminApprovalDate} onChange={onChange} isDark={isDark} />
            <FieldInput type="date" label="FC Expire" name="fcExpire" value={formData.fcExpire} onChange={onChange} isDark={isDark} />
            <FieldInput type="date" label="IC Expire" name="icExpire" value={formData.icExpire} onChange={onChange} isDark={isDark} />

            <FieldSelect
              label="Food For Driver"
              name="foodForDriver"
              value={formData.foodForDriver}
              onChange={onChange}
              isDark={isDark}
              options={[{ id: "", name: "Select" }, ...ddl.foodOptions]}
              idKey="id"
              nameKey="name"
            />

            <FieldSelect
              label="TDS %"
              name="tdsPercentId"
              value={formData.tdsPercentId}
              onChange={onChange}
              isDark={isDark}
              options={[{ id: "", name: "Select" }, ...ddl.tdsPercents]}
            />

            <FieldInput label="Cheque Favour Name" name="chequeFavourName" value={formData.chequeFavourName} onChange={onChange} isDark={isDark} />

            <div className="md:col-span-2">
              <FieldTextarea label="Remarks" name="remarks" value={formData.remarks} onChange={onChange} isDark={isDark} rows={3} />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Had Filling Hr
              </label>
              <RadioYesNo name="hasFillingHr" value={formData.hasFillingHr} onChange={onChange} isDark={isDark} />
            </div>
          </div>
        </Section>

        <Section title="Vehicle Basis" isDark={isDark}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldSelect
              label="Vehicle Basis *"
              name="vehicleBasisId"
              value={formData.vehicleBasisId}
              onChange={onChange}
              isDark={isDark}
              options={[{ id: "", name: "Select Vehicle Basis" }, ...ddl.vehicleBasis]}
            />

            <FieldInput label="Equipment SI No" name="vehicleSINumber" value={formData.vehicleSINumber} onChange={onChange} isDark={isDark} />
            <FieldInput type="number" label="Vehicle Rent" name="vehicleRent" value={formData.vehicleRent} onChange={onChange} isDark={isDark} />

            <FieldInput label="Owners Name" name="ownersName" value={formData.ownersName} onChange={onChange} isDark={isDark} />
            <FieldInput label="Contact Person" name="contactPerson" value={formData.contactPerson} onChange={onChange} isDark={isDark} />
            <FieldInput label="Mobile No" name="mobileNo" value={formData.mobileNo} onChange={onChange} isDark={isDark} />

            <FieldInput type="date" label="Valid From" name="validFrom" value={formData.validFrom} onChange={onChange} isDark={isDark} />
            <FieldInput type="date" label="Valid Upto" name="validUpto" value={formData.validUpto} onChange={onChange} isDark={isDark} />
            <FieldInput type="date" label="Permit Expire" name="permitExpire" value={formData.permitExpire} onChange={onChange} isDark={isDark} />

            <FieldSelect
              label="Fuel Type *"
              name="fuelTypeId"
              value={formData.fuelTypeId}
              onChange={onChange}
              isDark={isDark}
              options={[{ id: "", name: "Select Fuel Type" }, ...ddl.fuelTypes]}
            />

            <FieldInput label="PAN Card No" name="panCardNumber" value={formData.panCardNumber} onChange={onChange} isDark={isDark} />
            <FieldInput label="PAN Holder Name" name="panHolderName" value={formData.panHolderName} onChange={onChange} isDark={isDark} />

            <div className="md:col-span-2">
              <FieldTextarea label="Mailing Address" name="mailingAddress" value={formData.mailingAddress} onChange={onChange} isDark={isDark} rows={3} />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Had Filling KM
              </label>
              <RadioYesNo name="hasFillingKm" value={formData.hasFillingKm} onChange={onChange} isDark={isDark} />
            </div>
          </div>
        </Section>

        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClear}
            className="flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-4 h-4" />
            Clear Form
          </button>

          <div className="flex gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back
            </button>

            <button
              onClick={handleSave}
              className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass}`}
              disabled={loading}
            >
              <FaSave className="w-4 h-4" />
              {editVehicleId ? "Update" : "Save"} Details
            </button>
          </div>
        </div>

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default HireVehicleDetailsEntry;

/* =================== UI helpers =================== */
function Section({ title, isDark, children }) {
  return (
    <div className={`rounded-xl p-6 mb-8 border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
      <h2 className={`text-lg font-semibold mb-6 ${isDark ? "text-white" : "text-gray-900"}`}>{title}</h2>
      {children}
    </div>
  );
}

function baseInputClass(isDark) {
  return `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
}

function FieldInput({ label, name, value, onChange, isDark, type = "text" }) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{label}</label>
      <input type={type} name={name} value={value} onChange={onChange} className={baseInputClass(isDark)} />
    </div>
  );
}

function FieldTextarea({ label, name, value, onChange, isDark, rows = 3 }) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{label}</label>
      <textarea name={name} value={value} onChange={onChange} rows={rows} className={baseInputClass(isDark)} />
    </div>
  );
}

function FieldSelect({ label, name, value, onChange, isDark, options, idKey = "id", nameKey = "name" }) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{label}</label>
      <select name={name} value={value} onChange={onChange} className={baseInputClass(isDark)}>
        {options.map((o, idx) => (
          <option key={idx} value={o[idKey]}>
            {o[nameKey]}
          </option>
        ))}
      </select>
    </div>
  );
}

function RadioYesNo({ name, value, onChange, isDark }) {
  const opts = ["yes", "no"];
  return (
    <div className="flex gap-4">
      {opts.map((opt) => (
        <label key={opt} className="flex items-center gap-2">
          <input type="radio" name={name} value={opt} checked={value === opt} onChange={onChange} className="text-blue-600 focus:ring-blue-500" />
          <span className={isDark ? "text-gray-300" : "text-gray-700"}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
        </label>
      ))}
    </div>
  );
}